import { useState, useEffect } from 'react';
import AccountsMenu from './AccountsMenu';
import WeeklyStreakPopup from './WeeklyStreakPopup';
import { Bell, Search, Sparkles, PanelLeft, HelpCircle } from 'lucide-react';
import Notifications from '../sections/Notifications';
import { Dialog, DialogTrigger, DialogContent, DialogTitle } from '../ui/dialog';
import { useUser } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useToken } from '../../hooks/useToken';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import { useNavigate } from '@tanstack/react-router';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const TopBar = ({ expanded, toggleSidebar }) => {
    const [streakData, setStreakData] = useState({ number: 0, active: false });
    const [weeklyStreakData, setWeeklyStreakData] = useState([]);
    const [streakOpen, setStreakOpen] = useState(false);
    const { user } = useUser();
    const token = useToken();
    const navigate = useNavigate();
    const {
        data: notifications = [],
        isLoading: notificationsLoading,
        refetch: refetchNotifications,
    } = useQuery({
        queryKey: queryKeys.notifications({}),
        queryFn: () => api.getUserNotifications(token || '', {}),
        enabled: !!token && !!user?.id,
        select: d => d.notifications ?? d,
    });

    const { data: streakResult } = useQuery({
        queryKey: queryKeys.userStreak(user?.id),
        queryFn: () => api.getUserStreak(user?.id, token || ''),
        enabled: !!token && !!user?.id,
        select: d => {
            const data = d?.user_streak;
            if (!data || !data.streak_string) return { streakData: { number: 0, active: false }, weeklyData: [] };
            const xpPerDay = data.streak_string.split(',').map(Number);

            const getDayOfYear = date => {
                const start = new Date(date.getFullYear(), 0, 0);
                const diff = date - start;
                const oneDay = 1000 * 60 * 60 * 24;
                return Math.floor(diff / oneDay);
            };

            const todayIndex = getDayOfYear(new Date()) - 1;
            const activeToday = todayIndex < xpPerDay.length && xpPerDay[todayIndex] > 0;

            let currentStreak = 0;
            if (todayIndex < xpPerDay.length && xpPerDay[todayIndex] > 0) {
                currentStreak = 1;
                for (let i = todayIndex - 1; i >= 0; i--) {
                    if (xpPerDay[i] > 0) currentStreak++;
                    else break;
                }
            }

            const weeklyData = [];
            for (let i = 6; i >= 0; i--) {
                const dayIndex = todayIndex - i;
                weeklyData.push({ completed: dayIndex >= 0 && xpPerDay[dayIndex] > 0 });
            }

            return { streakData: { number: currentStreak, active: activeToday }, weeklyData };
        },
    });

    useEffect(() => {
        if (streakResult) {
            setStreakData(streakResult.streakData);
            setWeeklyStreakData(streakResult.weeklyData);
        }
    }, [streakResult]);

    useEffect(() => {
        if (notifications.length > 0) {
            const toastedNotificationIds = new Set(JSON.parse(localStorage.getItem('toastedNotificationIds') || '[]'));

            notifications.forEach(notification => {
                const isEnrolmentNotification =
                    notification.title === 'Course Enrolment' || notification.title === 'Pathway Enrolment';

                if (isEnrolmentNotification && !notification.is_read && !toastedNotificationIds.has(notification.id)) {
                    toast.success(notification.message, {
                        description: notification.title,
                        duration: 5000,
                    });
                    toastedNotificationIds.add(notification.id);
                }
            });

            localStorage.setItem('toastedNotificationIds', JSON.stringify(Array.from(toastedNotificationIds)));
        }
    }, [notifications]);

    const unreadCount = notifications.filter(n => !n.is_read).length;

    return (
        <>
            {/* Desktop TopBar */}
            <div
                className={`hidden md:flex h-16.25 px-8 py-4 border-b border-neutral-300 z-30 items-center justify-between fixed top-0 bg-neutral-50 transition-all duration-300 ${
                    expanded ? 'left-62.5 w-[calc(100%-250px)]' : 'left-17.5 w-[calc(100%-70px)]'
                }`}
            >
                <div className="flex items-center">
                    <div
                        onClick={toggleSidebar}
                        className="mr-4 w-8 h-8 rounded p-1 border-none flex justify-center items-center cursor-pointer hover:bg-neutral-200"
                    >
                        <PanelLeft className="w-6 h-6 text-neutral-700" />
                    </div>
                    <div className="relative hidden">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-700" />
                        <Input
                            placeholder="Search..." // TODO: return search bar when implementing algolia search
                            className={`pl-8 transition-all duration-300 ${expanded ? 'w-64' : 'w-48'}`}
                        />
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <Button
                        variant="destructive"
                        size="sm"
                        className="items-center gap-2 hidden" //TODO: return flex when AI module is ready
                        onClick={() => navigate('/coming-soon')}
                    >
                        <span>Ask AI</span>
                        <Sparkles className="text-neutral-700" />
                    </Button>
                    <button
                        type="button"
                        aria-label="Open FAQs"
                        className="relative p-2 rounded-md hover:bg-neutral-200 ease-in-out duration-200"
                        onClick={() => navigate({ to: '/faq' })}
                    >
                        <HelpCircle className="text-neutral-700" />
                    </button>
                    <Dialog open={streakOpen} onOpenChange={setStreakOpen}>
                        <WeeklyStreakPopup
                            streakData={weeklyStreakData}
                            currentStreak={streakData.number}
                            open={streakOpen}
                            onOpenChange={setStreakOpen}
                            isMobile={false}
                            isActive={streakData.active}
                        />
                    </Dialog>
                    <Dialog
                        onOpenChange={open => {
                            if (!open) {
                                refetchNotifications?.();
                            }
                        }}
                    >
                        <DialogTrigger className="relative p-2 rounded-md text-neutral-700 hover:bg-neutral-200 ease-in-out duration-200 border-l border-neutral-300">
                            <Bell />
                            {unreadCount > 0 && (
                                <span className="absolute -top-1 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-white p-1">
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                        </DialogTrigger>
                        <DialogContent
                            showCloseButton={false}
                            className="fixed right-10 top-16 h-auto w-96 max-w-lg border border-neutral-300 bg-background p-2 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right left-auto translate-x-0 translate-y-0 rounded-none"
                        >
                            <Notifications notifications={notifications} loading={notificationsLoading} />
                        </DialogContent>
                        <DialogTitle className="sr-only">Notifications</DialogTitle>
                    </Dialog>

                    <div className="rounded-full border-2 border-neutral-300">
                        <AccountsMenu isMorePage={false} />
                    </div>
                </div>
            </div>

            {/* Mobile TopBar */}
            <div className="md:hidden h-16.25 px-4 py-4 border-b border-neutral-300 z-40 flex items-center justify-between fixed top-0 w-full bg-neutral-50">
                <img src="/Zindua-Alt-Favicon.png" alt="Zindua LMS favicon" className="w-10 h-10" />
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        aria-label="Open FAQs"
                        className="relative p-2 rounded-md hover:bg-neutral-200 ease-in-out duration-200"
                        onClick={() => navigate({ to: '/faq' })}
                    >
                        <HelpCircle className="text-neutral-700" />
                    </button>
                    <WeeklyStreakPopup
                        streakData={weeklyStreakData}
                        currentStreak={streakData.number}
                        open={streakOpen}
                        onOpenChange={setStreakOpen}
                        isMobile={true}
                        isActive={streakData.active}
                    />
                    <button
                        type="button"
                        aria-label="Open notifications"
                        className="relative p-2 rounded-md hover:bg-neutral-200 ease-in-out duration-200 border-l border-neutral-300"
                        onClick={() => navigate({ to: '/notifications' })}
                    >
                        <Bell className="text-neutral-700" />
                        {unreadCount > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 md:top-1 md:right-1 md:p-1 flex h-5 w-5 md:h-6 md:w-6 items-center justify-center rounded-full bg-primary text-xs text-white">
                                {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                        )}
                    </button>
                    <AccountsMenu isMorePage={true} />
                </div>
            </div>
        </>
    );
};

export default TopBar;
